import { FieldValues, Form, FormProps, Modal, ModalProps, UseMelioFormResults } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';

import { FormWidgetProps } from '../types';

type TopLevelProps = {
  isLoading?: boolean;
  isOpen: boolean;
  onClose: VoidFunction;
};

export type FormDialogWidgetProps<T extends FieldValues, TMore = NonNullable<unknown>> = Omit<
  FormWidgetProps<T>,
  'onSubmissionStateChange'
> &
  TopLevelProps &
  TMore;

type DisabledButtonProps = { isLoading?: never; onClick?: never; isDisabled?: never };

type SecondaryButtonProps = Override<
  NonNullable<ModalProps['secondaryButton']>,
  { variant?: NonNullable<ModalProps['secondaryButton']>['variant'] } & DisabledButtonProps
>;
type PrimaryButtonProps = Override<
  NonNullable<ModalProps['primaryButton']>,
  { variant?: NonNullable<ModalProps['primaryButton']>['variant'] } & DisabledButtonProps
>;

export type FormDialogProps<T extends FieldValues = FieldValues> = TopLevelProps &
  Pick<ModalProps, 'header' | 'description' | 'icon' | 'isLoading'> &
  Pick<FormProps, 'size' | 'columns' | 'autoFocus'> & {
    useFormResults: Pick<UseMelioFormResults<T>, 'cancelButtonProps' | 'submitButtonProps' | 'formProps'>;
    children?: React.ReactNode;
    secondaryButton?: SecondaryButtonProps;
    primaryButton: PrimaryButtonProps;
  };

export const FormDialog = forwardRef<FormDialogProps>(
  (
    {
      onClose: _onClose,
      secondaryButton,
      primaryButton,
      useFormResults,
      size,
      columns,
      autoFocus = true,
      children,
      ...props
    },
    ref
  ) => {
    const isSaving = useFormResults.submitButtonProps.isLoading;
    const onClose = isSaving ? () => null : _onClose;
    return (
      <Modal
        shouldHideCloseButton={isSaving}
        data-component={FormDialog.displayName}
        onClose={onClose}
        primaryButton={{ variant: 'primary', ...primaryButton, ...useFormResults.submitButtonProps }}
        secondaryButton={
          secondaryButton && {
            variant: 'tertiary',
            ...secondaryButton,
            ...useFormResults.cancelButtonProps,
            onClick: onClose,
          }
        }
        {...props}
        ref={ref}
      >
        <Form {...useFormResults.formProps} size={size} columns={columns} autoFocus={autoFocus}>
          {children}
        </Form>
      </Modal>
    );
  }
);

FormDialog.displayName = 'FormDialog';
